import { useState, useEffect } from "react";
import { Card, Title, Label, Input, Overlay, TextArea, ModalCloseButton } from "../CommonComponents/DataCard";
import Spinner from "../CommonComponents/Spinner";
import { getAuthorById, updateAuthor } from "../../services/AuthorService";
import Button from "../CommonComponents/Button";
import { getCurrentUser } from "../../services/AuthService";
import { ROLES } from "../../services/Constants";
import MessageModal from "../CommonComponents/MessageModal";
import DatePicker from "../CommonComponents/DatePicker";

function isValidUrl(value) {
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

function EditAuthorModal({ isOpen, onClose, id, onAuthorUpdated }) {
    const [author, setAuthor] = useState(null);
    const [biographyType, setBiographyType] = useState("text"); // "text" | "url"
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [currentUser, setCurrentUser] = useState(null);
    const [message, setMessage] = useState(null);
    const [messageSuccess, setMessageSuccess] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);

    useEffect(() => {
        if (!isOpen || !id) return;

        const fetchData = async () => {
            setLoading(true);
            setError("");
            try {
                const userData = await getCurrentUser();
                if (userData.authenticated === true) {
                    setCurrentUser(userData);
                } else {
                    setCurrentUser(null);
                }
            } catch {
                setCurrentUser(null);
            }
            try {
                const authorData = await getAuthorById(id);
                setAuthor(authorData);
                // Determine biography type based on existing value
                if (authorData.biography && isValidUrl(authorData.biography)) {
                    setBiographyType("url");
                } else {
                    setBiographyType("text");
                }
            } catch (err) {
                setError(err.message || "Greška prilikom učitavanja autora");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [isOpen, id]);

    if (!isOpen) return null;
    if (!currentUser || !(currentUser.role === ROLES.EMPLOYEE || currentUser.role === ROLES.ADMIN)) {
        return null;
    }

    const handleChange = (e) => {
        setAuthor({ ...author, [e.target.name]: e.target.value });
    };

    const handleBiographyTypeChange = (type) => {
        setBiographyType(type);
        setAuthor((prev) => ({ ...prev, biography: "" }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError("");
        if (biographyType === "url" && author.biography && !isValidUrl(author.biography)) {
            setMessage("Unesena biografija nije ispravna URL adresa (mora počinjati s http:// ili https://).");
            setMessageSuccess(false);
            setShowMessageModal(true);
            setSaving(false);
            return;
        }
        try {
            const updated = await updateAuthor(id, author);

            setMessage("Autor je uspješno ažuriran!");
            setMessageSuccess(true);
            setShowMessageModal(true);

            setTimeout(() => {
                if (onAuthorUpdated) onAuthorUpdated(updated);
                onClose();
            }, 1500);
        } catch (err) {
            setMessage(err.message || "Greška pri spremanju autora.");
            setMessageSuccess(false);
            setShowMessageModal(true);
        } finally {
            setSaving(false);
        }
    };

    const handleCloseModal = () => {
        setShowMessageModal(false);
    };

    return (
        <Overlay onClick={onClose} style={{ alignItems: "flex-start", padding: "2rem 1rem", overflowY: "auto", boxSizing: "border-box" }}>
            <div onClick={e => e.stopPropagation()} style={{ width: "fit-content" }}>
                <Card style={{ position: "relative" }}>
                <ModalCloseButton onClick={onClose} title="Zatvori" style={{ position: "absolute", top: "0.75rem", right: "0.75rem" }}>×</ModalCloseButton>
                    <MessageModal
                        isOpen={showMessageModal}
                        onClose={handleCloseModal}
                        title="Uređivanje autora"
                        variant={messageSuccess ? "success" : "error"}
                    >
                        {message}
                    </MessageModal>

                    {loading ? (
                        <Spinner />
                    ) : error ? (
                        <div style={{ color: "red" }}>{error}</div>
                    ) : (
                        <>
                            <Title>Uredi autora: {author.name} {author.surname}</Title>

                            <Label>Ime:</Label>
                            <Input name="name" value={author.name} onChange={handleChange} />

                            <Label>Prezime:</Label>
                            <Input name="surname" value={author.surname} onChange={handleChange} />

                            <Label>Datum rođenja:</Label>
                            <DatePicker name="dateOfBirth" value={author.dateOfBirth || ""} onChange={handleChange} />

                            <Label>Datum smrti:</Label>
                            <DatePicker name="dateOfDeath" value={author.dateOfDeath || ""} onChange={handleChange} />

                            <Label>Mjesto rođenja:</Label>
                            <Input name="placeOfBirth" value={author.placeOfBirth} onChange={handleChange} />

                            <Label>Mjesto smrti:</Label>
                            <Input name="placeOfDeath" value={author.placeOfDeath || ""} onChange={handleChange} />

                            <Label>Vrsta unosa biografije:</Label>
                            <div style={{ display: "flex", gap: "16px", marginBottom: "8px" }}>
                                <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                                    <input
                                        type="radio"
                                        name="biographyType"
                                        value="text"
                                        checked={biographyType === "text"}
                                        onChange={() => handleBiographyTypeChange("text")}
                                    />
                                    Vlastiti tekst
                                </label>
                                <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                                    <input
                                        type="radio"
                                        name="biographyType"
                                        value="url"
                                        checked={biographyType === "url"}
                                        onChange={() => handleBiographyTypeChange("url")}
                                    />
                                    Poveznica (URL)
                                </label>
                            </div>

                            {biographyType === "url" ? (
                                <>
                                    <Label>URL biografije:</Label>
                                    <Input
                                        name="biography"
                                        value={author.biography || ""}
                                        onChange={handleChange}
                                        placeholder="https://..."
                                    />
                                </>
                            ) : (
                                <>
                                    <Label>Biografija:</Label>
                                    <TextArea
                                        name="biography"
                                        value={author.biography || ""}
                                        onChange={handleChange}
                                        placeholder="Unesite biografiju autora..."
                                        rows={6}
                                    />
                                </>
                            )}

                            <div style={{ display: "flex", gap: "8px", marginTop: "16px", justifyContent: "flex-end" }}>
                                <Button tekst="Odustani" akcija={onClose} />
                                <Button tekst={saving ? "Spremanje..." : "Spremi"} akcija={handleSave} />
                            </div>
                        </>
                    )}
                </Card>
            </div>
        </Overlay>
    );
}

export default EditAuthorModal;
